import { useUsersPreferredTemperature } from '@/authenticated/hooks/useUsersPreferredTemperature'
import { useAppSelector } from '@/hooks'
import { ID } from '@/types/model'
import { dateIsBetween } from '@/utils/time'
import { getDateFromTimestamp } from '@/utils/timeUtilities'
import { selectNextWithThermostatId } from '@/selectors/thermostatDemandResponseEvents'
import { ThermostatDemandResponseEvent } from '@/types'

export const mockEventExplanation =
  'You are currently participating in an event until 9:30 PM tonight. Your temperature may shift up to 3° outside your normal settings.'

export const mockPreCoolExplanation =
  'Your thermostat may start pre-cooling before your event from 5:00 PM to 8:00 PM. During the event, temperatures may shift up to 3°.'

// Inputs the copy needs, all primitives, so the string-building stays a pure function that can
// be unit tested without hooks or Redux (see CLAUDE.md guidance on extracting hook logic).
export type EventExplanationInput = {
  formattedStartTime: string
  formattedEndTime: string
  endIsInToday: boolean
  maxTempDelta: number
  eventIsCurrentlyActive: boolean
  enrolled: boolean
  hasPreCool: boolean
}

/**
 * Builds the banner/modal explanation copy for a thermostat DR event, including pre-cool
 * messaging. Pre-cool runs *before* the event, so when one is scheduled the upcoming copy hedges
 * with "may start pre-cooling" (it can be skipped if the home is already cool enough) and keeps
 * the pre-cool change distinct from the during-event temperature shift.
 */
export function buildEventExplanation(details: EventExplanationInput): string {
  const {
    formattedStartTime,
    formattedEndTime,
    endIsInToday,
    maxTempDelta,
    eventIsCurrentlyActive,
    enrolled,
    hasPreCool,
  } = details

  const tempShift = `Your temperature may shift up to ${maxTempDelta}° outside your normal settings.`
  const window = `from ${formattedStartTime} to ${formattedEndTime}${
    endIsInToday ? '' : ' tomorrow'
  }`

  // Event underway: pre-cool has already finished, so keep the existing messaging.
  if (eventIsCurrentlyActive && enrolled) {
    return `You are currently participating in an event until ${formattedEndTime}. ${tempShift}`
  }
  if (eventIsCurrentlyActive && !enrolled) {
    return `There is currently an event until ${formattedEndTime}. Temperatures may shift up to ${maxTempDelta}° outside your normal settings if you would like to participate.`
  }

  // Upcoming event, not enrolled.
  if (!enrolled) {
    return `There is an upcoming event ${window}. Temperatures may shift up to ${maxTempDelta}° outside your normal settings if you would like to participate.`
  }

  // Upcoming, enrolled, with a pre-cool scheduled. Keep the pre-cool change (before) distinct from
  // the event's temperature shift (during), and hedge with "may" since it can be skipped.
  if (hasPreCool) {
    return `Your thermostat may start pre-cooling before your event ${window}. During the event, temperatures may shift up to ${maxTempDelta}°.`
  }

  // Upcoming event, enrolled, no pre-cool.
  return `You have an upcoming event ${window}. ${tempShift}`
}

function useEventDetails(event: ThermostatDemandResponseEvent | undefined) {
  if (!event) {
    return undefined
  }
  const formattedStartTime = getDateFromTimestamp(
    event.start_datetime,
    'h:mm A',
  )
  const formattedEndTime = getDateFromTimestamp(event.end_datetime, 'h:mm A')
  const endIsInToday =
    new Date(event.end_datetime).getDate() === new Date().getDate()
  const maxTempDelta = useUsersPreferredTemperature(
    event.max_temp_delta_f,
    'F',
  ).value

  const eventIsCurrentlyActive = dateIsBetween({
    date: new Date(),
    start: event?.start_datetime,
    end: event?.end_datetime,
  })

  const hasPreCool = Boolean(event.pre_cool_event)

  return {
    formattedStartTime,
    formattedEndTime,
    endIsInToday,
    maxTempDelta,
    eventIsCurrentlyActive,
    enrolled: event.enrolled,
    hasPreCool,
    event,
  }
}

export function useEventExplanation(thermostatId: ID) {
  const nextEvent = useAppSelector((state) =>
    selectNextWithThermostatId(state, thermostatId),
  )

  const eventDetails = useEventDetails(nextEvent)

  if (!eventDetails) {
    return 'No Upcoming Events'
  }

  return buildEventExplanation(eventDetails)
}

export function useEnrolledEventDetails(thermostatId: ID) {
  const nextEvent = useAppSelector((state) =>
    selectNextWithThermostatId(state, thermostatId),
  )

  const eventDetails = useEventDetails(nextEvent)

  if (!eventDetails || !eventDetails?.event.enrolled) {
    return {
      title: 'There was an issue retrieving your event details.',
      details: 'Please try again later.',
    }
  }

  const {
    formattedStartTime,
    formattedEndTime,
    maxTempDelta,
    eventIsCurrentlyActive,
    hasPreCool,
  } = eventDetails

  if (eventIsCurrentlyActive) {
    return {
      title: 'Great! Thanks for participating in this event.',
      details: `This event lasts until ${formattedEndTime}. Your temperature may shift up to ${maxTempDelta}° outside your normal settings.`,
    }
  }

  const preCoolNote = hasPreCool
    ? ' Your thermostat may start pre-cooling beforehand.'
    : ''

  return {
    title: 'Great! Thanks for participating in the upcoming event.',
    details: `This event starts at ${formattedStartTime}.${preCoolNote} Your temperature may shift up to ${maxTempDelta}° outside your normal settings.`,
  }
}
